import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { useForm, FormProvider } from 'react-hook-form';
import { useGraphEditorStore } from '@/graph-store';
import { isRootMaterial } from '@/graph/domain/topology';
import { cloneGraphData } from '@/graph-store/actions/snapshots';
import { propagateDownstream } from '@/commands/helpers/propagateDownstream';
import { computeOverflow, type TransferRowFormState } from '@/graph-store/actions/transferHelpers';
import { QuantitySpecForm } from '../AddMaterialModal/QuantitySpecForm';
import { ContainerSpecForm } from '../AddMaterialModal/ContainerSpecForm';
import { ChooseWellAddressDialog } from '../AddMaterialModal/ChooseWellAddressDialog';
import type { MaterialNodeSpec, Unit, QuantityType, ContainerType } from '@/graph/domain/types';

export interface EditMaterialModalProps {
  materialId: string;
  onClose: () => void;
  onSaved?: () => void;
}

interface EditMaterialFormValues {
  name: string;
  isEmpty: boolean;
  amount: number;
  units: string;
  quantityType: string;
  containerType: string;
  containerName: string;
  containerVolumeAmount: number;
  containerVolumeUnits: string;
  vendor: string;
  plateName: string;
  sameAsMaterial: boolean;
  rows: number;
  columns: number;
  wellVolumeAmount: number;
  wellVolumeUnits: string;
  wellAddresses: Array<{ name: string; row: string; column: string }>;
  notes: string;
}

function specToForm(spec: MaterialNodeSpec): EditMaterialFormValues {
  const rawVolume = spec.container?.volume;
  const volumeAmount = typeof rawVolume === 'object'
    ? (rawVolume?.amount ?? 0)
    : Number(rawVolume ?? 0) || 0;
  const volumeUnit = typeof rawVolume === 'object'
    ? (rawVolume?.unit ?? 'mL')
    : 'mL';

  return {
    name: spec.name ?? '',
    isEmpty: spec.isEmpty ?? false,
    amount: spec.quantity?.value?.amount ?? 0,
    units: (spec.quantity?.value?.units ?? 'mL') as string,
    quantityType: spec.quantity?.type ?? 'Volume',
    containerType: spec.container?.containerType ?? 'Tube',
    containerName: spec.container?.name ?? '',
    containerVolumeAmount: volumeAmount,
    containerVolumeUnits: volumeUnit,
    vendor: spec.container?.make ?? '',
    plateName: spec.container?.name ?? '',
    sameAsMaterial: spec.container?.copyName ?? false,
    rows: spec.container?.rows ?? 0,
    columns: spec.container?.columns ?? 0,
    wellVolumeAmount: spec.container?.wellVolume?.amount ?? 0,
    wellVolumeUnits: (spec.container?.wellVolume?.units ?? 'µL') as string,
    wellAddresses: spec.container?.wellAddresses ?? [],
    notes: spec.notes ?? '',
  };
}

export function EditMaterialModal({ materialId, onClose, onSaved }: EditMaterialModalProps) {
  const material = useGraphEditorStore((s) => s.graphData.materialNodes[materialId]);
  const flowEdges = useGraphEditorStore((s) => s.graphData.flowEdges);
  const graphData = useGraphEditorStore((s) => s.graphData);
  const editMaterial = useGraphEditorStore((s) => s.editMaterial);

  const [wellDialogOpen, setWellDialogOpen] = React.useState(false);

  const methods = useForm<EditMaterialFormValues>({
    defaultValues: material ? specToForm(material) : undefined,
    mode: 'onBlur',
  });

  const { handleSubmit, register, watch, setValue, formState: { errors } } = methods;

  const containerType = watch('containerType');
  const rows = watch('rows');
  const columns = watch('columns');
  const wellAddresses = watch('wellAddresses');
  const isPlate = containerType === 'Well Plate';

  const downstreamImpactCount = React.useMemo(() => {
    if (!material) return 0;
    const previewGraph = cloneGraphData(graphData);
    const result = propagateDownstream(previewGraph, materialId);
    return result.changedMaterialIds.size;
  }, [graphData, material, materialId]);

  const quantityStarvationWarning = React.useMemo(() => {
    if (!material) return null;
    const watchedIsEmpty = watch('isEmpty');
    const watchedAmount = Number(watch('amount'));
    const watchedUnits = watch('units') as Unit;

    const effectiveAmount = watchedIsEmpty ? 0 : (Number.isFinite(watchedAmount) ? watchedAmount : 0);
    const draftRoot: MaterialNodeSpec = {
      ...material,
      quantity: watchedIsEmpty
        ? null
        : {
            type: (watch('quantityType') as QuantityType) || (material.quantity?.type ?? 'Volume'),
            value: {
              amount: effectiveAmount,
              units: watchedUnits || (material.quantity?.value?.units ?? 'mL'),
            },
          },
    };

    const transferRows: TransferRowFormState[] = [];
    for (const edge of Object.values(graphData.flowEdges)) {
      if (edge.edgeKind === 'graphsLink') continue;
      if (edge.sourceId !== materialId) continue;

      const op = graphData.operationNodes[edge.targetId];
      if (!op || op.archetype !== 'TRANSFER' || !op.transferRow?.quantity?.value) continue;

      transferRows.push({
        id: op.transferRow.id,
        mode: 'numeric',
        amount: op.transferRow.quantity.value.amount,
        unit: op.transferRow.quantity.value.units,
        symbolic: null,
        destinationMaterialId: op.transferRow.destinationMaterialId ?? null,
        destinationDraft: null,
      });
    }

    if (transferRows.length === 0) return null;

    const overflow = computeOverflow(draftRoot, transferRows);
    if (!overflow?.hasOverflow) return null;

    return `Warning: Reducing quantity may starve downstream transfers (${overflow.totalTransferred.toFixed(2)} requested, ${overflow.available.toFixed(2)} available).`;
  }, [graphData, material, materialId, watch]);

  if (!material) return null;
  if (!isRootMaterial(materialId, flowEdges)) return null;

  const onSubmit = (data: EditMaterialFormValues) => {
    const patch: Partial<MaterialNodeSpec> = {
      name: data.name,
      isEmpty: data.isEmpty,
      quantity: {
        value: { amount: data.isEmpty ? 0 : data.amount, units: data.units as Unit },
        type: data.quantityType as QuantityType,
      },
      container: {
        ...material.container,
        id: material.container?.id,
        name: data.sameAsMaterial ? data.name : data.containerName,
        containerType: data.containerType as ContainerType,
        volume: data.containerVolumeAmount > 0
          ? { amount: data.containerVolumeAmount, unit: data.containerVolumeUnits as Unit }
          : undefined,
        make: data.vendor || undefined,
        copyName: data.sameAsMaterial,
        rows: data.rows || undefined,
        columns: data.columns || undefined,
        wellVolume: data.wellVolumeAmount > 0
          ? { amount: data.wellVolumeAmount, units: data.wellVolumeUnits as Unit }
          : undefined,
        wellAddresses: data.wellAddresses?.length ? data.wellAddresses : undefined,
      },
      notes: data.notes || undefined,
    };
    editMaterial(materialId, patch);
    onSaved?.();
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="edit-material-modal"
      slotProps={{
        paper: {
          sx: {
            width: '878px',
            maxWidth: 'calc(100vw - 32px)',
            height: '573px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '4px',
            overflow: 'hidden',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          height: '65px',
          minHeight: '65px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: '16px',
          py: 0,
          borderBottom: '1px solid #dee2e6',
          fontSize: '16px',
          fontWeight: 500,
          color: '#1f4f7a',
          backgroundColor: '#f8f9fc',
        }}
      >
        Edit Material

        <IconButton
          onClick={onClose}
          size="small"
          aria-label="close"
          sx={{
            color: '#222',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <DialogContent
            sx={{
              px: '32px',
              py: '30px',
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
              display: 'flex',
              justifyContent: 'center',

              '&::-webkit-scrollbar': {
                width: '8px',
              },

              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#999',
                borderRadius: '4px',
              },
            }}
          >
            <Box
              sx={{
                maxWidth: '640px',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '170px 1fr',
                  alignItems: 'center',
                  columnGap: '10px',
                  mb: '24px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    textAlign: 'right',
                  }}
                >
                  Name
                  <Box
                    component="span"
                    sx={{ color: '#dc3545', ml: '2px' }}
                  >
                    *
                  </Box>
                </Typography>

                <TextField
                  {...register('name', { required: 'Name is required' })}
                  size="small"
                  fullWidth
                  placeholder="Enter Name"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  autoFocus
                  data-testid="name-input"
                />
              </Box>

              <Typography variant="body2" color="text.secondary" data-testid="downstream-impact-summary" sx={{ mb: 2 }}>
                {downstreamImpactCount} downstream materials will be updated.
              </Typography>

              {quantityStarvationWarning && (
                <Typography variant="body2" color="warning.main" data-testid="quantity-starvation-warning" sx={{ mb: 2 }}>
                  {quantityStarvationWarning}
                </Typography>
              )}

              <Box sx={{ mb: '24px' }}>
                <QuantitySpecForm />
              </Box>

              <Box sx={{ mb: '24px' }}>
                <ContainerSpecForm />

                {isPlate && rows > 0 && columns > 0 && (
                  <Button
                    variant="outlined"
                    onClick={() => setWellDialogOpen(true)}
                    data-testid="choose-well-btn"
                    sx={{ mt: 2 }}
                  >
                    Choose Well Address ({wellAddresses.length} selected)
                  </Button>
                )}
              </Box>

              {material.sourceMaterials && material.sourceMaterials.length > 0 && (
                <Box sx={{ mb: '24px' }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 1 }}>
                    Provenance (read-only)
                  </Typography>
                  {material.sourceMaterials.map((sm) => (
                    <Typography key={sm.sourceVertexId} variant="body2" color="text.secondary">
                      &bull; {sm.name}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              height: '76px',
              minHeight: '76px',
              px: '32px',
              borderTop: '1px solid #dee2e6',
              backgroundColor: '#fff',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              onClick={onClose}
              variant="outlined"
              data-testid="cancel-btn"
              sx={{
                minWidth: '93px',
                height: '38px',
                textTransform: 'none',
                borderColor: '#1976d2',
                color: '#1976d2',
                fontSize: '14px',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="outlined"
              startIcon={<SaveIcon />}
              data-testid="save-btn"
              sx={{
                minWidth: '93px',
                height: '38px',
                textTransform: 'none',
                borderColor: '#1976d2',
                color: '#1976d2',
                fontSize: '14px',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              Save
            </Button>
          </DialogActions>

          {wellDialogOpen && (
            <ChooseWellAddressDialog
              open
              rows={rows}
              columns={columns}
              selected={wellAddresses}
              onConfirm={(wells) => {
                setValue('wellAddresses', wells);
                setWellDialogOpen(false);
              }}
              onClose={() => setWellDialogOpen(false)}
            />
          )}
        </Box>
      </FormProvider>
    </Dialog>
  );
}

export default EditMaterialModal;
